import { useCallback } from 'react'
import { useJamesApolloQuery } from 'hooks/useJamesApolloQuery'
import useSelectedStatesContext, {
  useSelectedStatesActionsContext,
} from 'pages/AbsencePlanner/contexts/selectedStatesContext'
import { taskQueryAbsences } from 'pages/Tasks/queries'
import type { AbsenceRequestTaskT, TaskT } from 'pages/Tasks/types'
import type { Task } from 'shared/graphql/graphql'
import type { PersonT } from 'types'
import useAbsenceRequestDetails from '../../absence-request-details/use-absence-request-details'
import type { TaskDetailContentProps } from '../types'

export const useTaskDetailContent = ({
  task,
  refetch,
  selectedTaskUuid,
  refetchTask,
  onGoBack,
}: Pick<TaskDetailContentProps, 'task' | 'refetch' | 'selectedTaskUuid' | 'refetchTask' | 'onGoBack'>) => {
  const {
    timelineItems,
    showConflicts,
    showAbsencePlannerDrawer,
    setShowAbsencePlannerDrawer,
    person,
    isBalanceDrawerOpen,
    setIsBalanceDrawerOpen,
    dateFrom,
    dateTo,
    setShowForm,
    showForm,
    showDeleteButton,
    onClickDeleteRequest,
  } = useAbsenceRequestDetails(task as Task, refetch)
  const { selectedPerson } = useSelectedStatesContext()
  const { deselectDays, setSelectedPerson } = useSelectedStatesActionsContext()
  const { refetch: refetchAbsence } = useJamesApolloQuery<{ task: TaskT<AbsenceRequestTaskT> }>(taskQueryAbsences, {
    variables: { favurUuid: selectedTaskUuid },
    fetchPolicy: 'no-cache',
    skip: selectedTaskUuid === null,
    onCompleted(data) {
      const fetchedTask = data.task

      if (!selectedPerson) {
        setSelectedPerson(fetchedTask?.person as PersonT)
      }
    },
  })
  const onAccept = useCallback(
    (taskToRefetch: TaskT<AbsenceRequestTaskT>) => {
      refetchAbsence()

      if (taskToRefetch?.person?.id) {
        refetchTask?.(
          taskToRefetch.person?.id.toString(),
          taskToRefetch.taskData.dateFrom,
          taskToRefetch.taskData.dateTo,
        )
      }

      refetch()
      setShowForm(false)
    },
    [refetch, refetchAbsence, refetchTask, setShowForm],
  )
  const onDelete = () => {
    deselectDays()
    const taskToDelete = task as TaskT<AbsenceRequestTaskT>

    if (taskToDelete?.person?.id) {
      refetchTask?.(taskToDelete.person?.id.toString(), taskToDelete.taskData.dateFrom, taskToDelete.taskData.dateTo)
    }

    onGoBack()
  }

  return {
    dateTo,
    dateFrom,
    isBalanceDrawerOpen,
    onAccept,
    onDelete,
    person,
    setIsBalanceDrawerOpen,
    setShowAbsencePlannerDrawer,
    setShowForm,
    showAbsencePlannerDrawer,
    showConflicts,
    showForm,
    timelineItems,
    showDeleteButton,
    onClickDeleteRequest,
  }
}
